// Mic preflight for test calls, so a blocked microphone never starts (and pays for) a run.

export type MicrophoneCheck =
  | 'ok'
  | 'frame_blocked'
  | 'denied'
  | 'no_device'
  | 'busy'
  | 'unsupported'

interface FeaturePolicyLike {
  allowsFeature?: (feature: string) => boolean
}

interface PolicyDocument {
  permissionsPolicy?: FeaturePolicyLike
  featurePolicy?: FeaturePolicyLike
}

export const MICROPHONE_ERROR_TEXT: Record<Exclude<MicrophoneCheck, 'ok'>, string> = {
  frame_blocked:
    'This page blocks the microphone for the agent builder. Ask the site owner to allow it.',
  denied: "Microphone access is blocked. Allow it in your browser's site settings and try again.",
  no_device: 'No microphone found. Connect one and try again.',
  busy: 'Your microphone is in use by another app. Close it and try again.',
  unsupported: "This browser can't use the microphone here.",
}

// Chrome and Edge report a host page that withheld the microphone from this frame. Other browsers
// can't tell, and their block surfaces as NotAllowedError, which reads as denied.
export const frameAllowsMicrophone = (doc: PolicyDocument): boolean | undefined => {
  const policy = doc.permissionsPolicy ?? doc.featurePolicy
  return typeof policy?.allowsFeature === 'function'
    ? policy.allowsFeature('microphone')
    : undefined
}

export const classifyMicrophoneError = (error: unknown): Exclude<MicrophoneCheck, 'ok'> => {
  switch ((error as { name?: unknown } | null)?.name) {
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'no_device'
    case 'NotReadableError':
    case 'AbortError':
      return 'busy'
    default:
      return 'denied'
  }
}

// Opens and releases the mic once. The grant lasts for the page, so the call's own request does
// not prompt again.
export const checkMicrophone = async (
  doc: PolicyDocument = document as unknown as PolicyDocument,
  devices: Pick<MediaDevices, 'getUserMedia'> | undefined = navigator.mediaDevices,
): Promise<MicrophoneCheck> => {
  if (frameAllowsMicrophone(doc) === false) {
    return 'frame_blocked'
  }
  if (typeof devices?.getUserMedia !== 'function') {
    return 'unsupported'
  }
  try {
    const stream = await devices.getUserMedia({ audio: true })
    for (const track of stream.getTracks()) {
      track.stop()
    }
    return 'ok'
  } catch (error) {
    return classifyMicrophoneError(error)
  }
}
